import * as Sentry from '@sentry/react';

type ErrorContext = {
  /** Domain/feature area (e.g., 'billing', 'upload', 'auth') */
  domain?: string;
  /** The operation that failed (e.g., 'createCheckout', 'uploadFile') */
  operation?: string;
  /** Additional context data */
  [key: string]: unknown;
};

/**
 * Check if Sentry is enabled (not in development mode)
 */
const isSentryEnabled = (): boolean => {
  // Check if Sentry client is initialized
  return Sentry.getClient() !== null;
};

/**
 * Capture an exception to Sentry with structured context
 * Use this in catch blocks for errors that should be tracked as Issues
 */
export const captureError = (error: unknown, context?: ErrorContext): void => {
  // Only capture if Sentry is enabled (not in development)
  if (!isSentryEnabled()) {
    return;
  }
  
  const { domain, operation, ...extra } = context || {};
  
  Sentry.captureException(error, {
    tags: {
      ...(domain && { domain }),
      ...(operation && { operation }),
    },
    extra,
  });
};

/**
 * Wrap an async function to automatically capture errors to Sentry
 * Useful for service methods and API calls
 * 
 * @example
 * const result = await withErrorCapture(
 *   () => api.createCheckout(data),
 *   { domain: 'billing', operation: 'createCheckout' }
 * );
 */
export const withErrorCapture = async <T>(
  fn: () => Promise<T>,
  context?: ErrorContext
): Promise<T> => {
  try {
    return await fn();
  } catch (error) {
    captureError(error, context);
    throw error;
  }
};

/**
 * Create a wrapped version of an async function that auto-captures errors
 * Useful for creating instrumented service methods
 * 
 * @example
 * const wrappedFetch = wrapWithErrorCapture(
 *   fetchData,
 *   { domain: 'api', operation: 'fetchData' }
 * );
 */
export const wrapWithErrorCapture = <TArgs extends unknown[], TResult>(
  fn: (...args: TArgs) => Promise<TResult>,
  context?: ErrorContext
): ((...args: TArgs) => Promise<TResult>) => {
  return async (...args: TArgs): Promise<TResult> => {
    try {
      return await fn(...args);
    } catch (error) {
      captureError(error, context);
      throw error;
    }
  };
};

/**
 * Set user context for Sentry (call after login)
 */
export const setUserContext = (user: { id: string; email?: string }): void => {
  if (!isSentryEnabled()) {
    return;
  }
  Sentry.setUser({
    id: user.id,
    email: user.email,
  });
};

/**
 * Clear user context (call after logout)
 */
export const clearUserContext = (): void => {
  if (!isSentryEnabled()) {
    return;
  }
  Sentry.setUser(null);
};

/**
 * Set organization context for Sentry
 * Call when user selects/loads an organization
 */
export const setOrganizationContext = (org: {
  id: string;
  name?: string;
  plan?: string;
}): void => {
  if (!isSentryEnabled()) {
    return;
  }
  Sentry.setContext('organization', {
    id: org.id,
    name: org.name,
    plan: org.plan,
  });
};

/**
 * Set project context for Sentry
 * Call when user opens a project
 */
export const setProjectContext = (project: {
  id: string;
  name?: string;
}): void => {
  if (!isSentryEnabled()) {
    return;
  }
  Sentry.setContext('project', {
    id: project.id,
    name: project.name,
  });
};

/**
 * Set scene context for Sentry
 * Call when user enters the editor
 */
export const setSceneContext = (scene: {
  id: string;
  name?: string;
  storyId?: string;
}): void => {
  if (!isSentryEnabled()) {
    return;
  }
  Sentry.setContext('scene', {
    id: scene.id,
    name: scene.name,
    storyId: scene.storyId,
  });
};

/**
 * Clear all additional context (org, project, scene)
 * Call when navigating away from editor or logging out
 */
export const clearAdditionalContext = (): void => {
  if (!isSentryEnabled()) {
    return;
  }
  Sentry.setContext('organization', null);
  Sentry.setContext('project', null);
  Sentry.setContext('scene', null);
};

/**
 * Capture user feedback/bug report to Sentry
 * Creates a user feedback event linked to the current session
 */
export const captureUserFeedback = (feedback: {
  name?: string;
  email: string;
  message: string;
  associatedEventId?: string;
}): void => {
  if (!isSentryEnabled()) {
    return;
  }
  
  // Capture as a message event with user feedback context
  const eventId = feedback.associatedEventId || Sentry.captureMessage(
    `User Feedback: ${feedback.message.substring(0, 50)}...`,
    'info'
  );

  // Attach user feedback to the event
  Sentry.captureFeedback({
    associatedEventId: eventId,
    name: feedback.name || 'Anonymous',
    email: feedback.email,
    message: feedback.message,
  });
};

/**
 * Get the last Sentry event ID (useful for linking feedback to errors)
 */
export const getLastEventId = (): string | undefined => {
  if (!isSentryEnabled()) {
    return undefined;
  }
  return Sentry.lastEventId();
};

